/**
 * PLM Daily QC — the list (G5 Analysis ▸ PLM Daily QC, ListView
 * PlmV2DailyQc_ListView, PlmV2DailyListController; added 2026-10-16).
 *
 * Every Daily QC read, newest Daily QC ID first, with G5's two filters —
 * Analyst and Result (All · Pass · Fail · Pending). New starts today's read
 * (today's open read is offered first, as G5 New does); double-click opens a
 * read on the shared Daily QC screen. The same reads also show as Reference
 * rows on PLM ▸ QC; this entry is the direct route G5 has in its menu.
 *
 * Delete (G5 PlmV2DailyListDelete, 2026-10-17): a toolbar button — no row button.
 * Same rule, endpoint, reason and audit as Delete on the Daily QC screen: a pending
 * read no PLM layer uses, the analyst's own (an administrator's: any); gated by the
 * nav Delete right. Several ticked reads are deleted together with ONE reason; a
 * ticked read that may not be deleted is named and left (owner 2026-10-19, G5 parity).
 *
 * New is disabled while the analyst's latest Daily QC of today is not submitted —
 * the rule the Create procedure refuses by, read from the status endpoint's
 * `pendingDailyQcId` (owner 2026-10-19, G5 parity: PlmV2DailyUi.GateNew).
 *
 * Edit (owner 2026-10-19, G5 parity: PlmV2DailyListEdit): a toolbar button only (no
 * row icon), enabled only when exactly one ticked read is the analyst's own and not
 * submitted (the tooltip says why it is off); it opens the read for entry (the Daily
 * QC screen opens a read from the list in Enter mode — what the form's Edit, now
 * removed, did).
 *
 * Rollback (owner 2026-10-19, G5 parity: PlmV2DailyListRollback, as the QC Batch list
 * has it): enabled only when exactly one ticked read is the analyst's own SUBMITTED
 * read; asks G5's reason prompt, runs the form's Rollback (same endpoint and reason),
 * refreshes the list and today's status and opens the read for entry.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../../../auth/store';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { ComboSelect } from '../../../components/ComboSelect';
import { useConfirm } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { runLoading } from '../../../components/loading';
import { runWithAuditReason } from '../../../api/client';
import { useNavActions } from '../../../hooks/useNavActions';
import { FormTitle } from '../../../components/FormTitle';
import { useHotkeys, formatCombo, effectiveCombo, type HotkeyDef } from '../../../components/hotkeys';
import { fmtLocalDateTime } from '../../../utils/datetime';
import {
  deletePlmDailyQc, fetchPlmDailyQc, rollbackPlmDailyQc, fetchPlmDailyQcList, fetchPlmDailyQcStatus, type PlmDailyQcRow, type PlmDailyQcStatus,
} from './plm.api';
import {
  DAILY_DELETE_NONE, DAILY_DELETE_REASON, DAILY_RESULT_FILTERS, dailyAnalystOptions, dailyDeleteRefusal,
  dailyDeletedText, dailyGateStrip, dailyListActionGate, dailyListEditRefusal, dailyListRollbackPrompt,
  dailyListRollbackGate, dailyListRollbackRefusal, dailyMultiDeleteMessage, dailyNewBlockedTip, dailyNotDeletedText, dailyOrder,
  dailyResult, dailyStatusText, filterDailyRows, refusalText, searchDailyRows, type DailyResultFilter,
} from './plmDailyQc';
import PlmDailyQcPage from './PlmDailyQcPage';
import { PlmDailySlidePickerDialog } from './PlmDailySlidePickerDialog';
import './plm.css';

const PAGE_ID = 'plm-dailyqc';

export default function PlmDailyQcListPage() {
  const { confirm, prompt } = useConfirm();
  const notify = useNotify();
  const perm = useNavActions(PAGE_ID);
  const me = useAuthStore(s => s.user?.id ?? null);
  const [rows, setRows] = useState<PlmDailyQcRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [analyst, setAnalyst] = useState('');
  const [result, setResult] = useState<DailyResultFilter>('All');
  const [selected, setSelected] = useState<string | null>(null);
  // The ticked rows — what Delete acts on (several together, owner 2026-10-19).
  const [ticked, setTicked] = useState<Set<string | number>>(new Set());
  const [search, setSearch] = useState('');
  // Today's Daily QC of the signed-in analyst — the strip under the title (G5 GateHtml).
  const [status, setStatus] = useState<PlmDailyQcStatus | null>(null);
  // The open read; undefined = the list.
  const [open, setOpen] = useState<{ oid: string | null } | undefined>(undefined);
  // G5 New opens the "Reference Slide" window as a popup OVER the list.
  const [picking, setPicking] = useState(false);

  const load = useCallback(async () => {
    const t0 = performance.now();
    setLoading(true);
    try {
      const [list, st] = await Promise.all([fetchPlmDailyQcList(), fetchPlmDailyQcStatus().catch(() => null)]);
      setRows(dailyOrder(list));
      setStatus(st);
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - t0);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const analysts = useMemo(() => dailyAnalystOptions(rows), [rows]);
  const shown = useMemo(() => searchDailyRows(filterDailyRows(rows, analyst, result), search),
    [rows, analyst, result, search]);
  const strip = status ? dailyGateStrip(status) : null;
  // Result chips with their counts (standard list, like QC Batch's Status chips — owner 2026-10-19):
  // counted over the Analyst filter and the search, so each chip says what it would show.
  const resultCounts = useMemo(() => DAILY_RESULT_FILTERS.map(f =>
    [f, searchDailyRows(filterDailyRows(rows, analyst, f), search).length] as const), [rows, analyst, search]);

  /** G5 New: today's open read is offered first, otherwise the slide picker. */
  const doNew = useCallback(async () => {
    const status = await fetchPlmDailyQcStatus();
    const today = status?.state === 'pending' ? status.today : null;
    if (today && await confirm({
      title: 'New',
      message: `You already have today's Daily QC ${today.dailyQcId ?? ''} in progress. Open it instead?`,
    })) {
      setOpen({ oid: today.oid });
      return;
    }
    if (!today) setPicking(true);
  }, [confirm]);

  // New is off while the analyst's latest read of today is not submitted (owner 2026-10-19, G5 GateNew).
  const pendingId = status?.pendingDailyQcId ?? null;

  /** G5 Delete_Execute (owner 2026-10-19): the ticked reads (else the current one).
   *  Each is checked by the server's per-read rule first; the deletable ones are
   *  deleted with ONE reason (X-Audit-Reason, each audited as today), the others
   *  are named in the prompt and left. The server re-checks every rule. */
  const doDelete = useCallback(async () => {
    const oids = ticked.size > 0 ? [...ticked].map(String) : selected ? [selected] : [];
    const targets = oids.map(o => rows.find(r => r.oid === o)).filter((r): r is PlmDailyQcRow => !!r);
    if (targets.length === 0) return;
    let refusals: (string | null)[];
    try {
      const details = await Promise.all(targets.map(r => fetchPlmDailyQc(r.oid)));
      refusals = details.map(d => (d ? dailyDeleteRefusal(d) : 'This Daily QC is no longer there — press Refresh.'));
    } catch (e) {
      notify.error(refusalText(e, 'The Daily QC could not be read. Try Refresh, and if it keeps happening report it.'));
      return;
    }
    const allowed = targets.filter((_, i) => !refusals[i]);
    const refused = targets.filter((_, i) => !!refusals[i]).map(r => r.dailyQcId ?? '?');
    if (allowed.length === 0) {
      notify.error(targets.length === 1 ? refusals[0]! : DAILY_DELETE_NONE);
      return;
    }
    const reason = (await prompt({
      title: 'Delete',
      message: dailyMultiDeleteMessage(allowed.map(r => r.dailyQcId ?? '?'), refused),
      placeholder: DAILY_DELETE_REASON.placeholder,
      required: true,
      confirmLabel: 'Delete',
    }))?.trim();
    if (!reason) return;
    const deleted: string[] = [];
    const failed: string[] = [];
    await runLoading(runWithAuditReason(reason, async () => {
      for (const r of allowed) {
        try { await deletePlmDailyQc(r.oid); deleted.push(r.dailyQcId ?? '?'); }
        catch { failed.push(r.dailyQcId ?? '?'); }
      }
    }), { blocking: true, label: 'Deleting…' });
    setTicked(new Set());
    if (selected && allowed.some(r => r.oid === selected)) setSelected(null);
    await load();
    failed.push(...refused);
    if (deleted.length > 0) notify.success(dailyDeletedText(deleted));
    if (failed.length > 0) notify.warning(dailyNotDeletedText(failed));
  }, [ticked, selected, rows, notify, prompt, load]);
  const deleteTarget = ticked.size > 0 || (!!selected && rows.some(r => r.oid === selected));

  // Edit / Rollback act on exactly one ticked read (owner 2026-10-19, G5 parity).
  const oneRow = ticked.size === 1 ? rows.find(r => r.oid === String([...ticked][0])) ?? null : null;
  const editGate = dailyListActionGate('edit', ticked.size, oneRow, me);
  const tickedRows = useMemo(() => rows.filter(r => ticked.has(r.oid)), [rows, ticked]);
  const rollbackGate = dailyListRollbackGate(tickedRows);
  const [rollingBack, setRollingBack] = useState(false);

  /** G5 PlmV2DailyListEdit: the analyst's own read that is not submitted opens for entry. */
  const doEdit = useCallback(() => {
    if (ticked.size > 1) { notify.error('Tick one Daily QC to edit.'); return; }
    if (!oneRow) return;
    const refusal = dailyListEditRefusal(oneRow, me);
    if (refusal) { notify.error(refusal); return; }
    setOpen({ oid: oneRow.oid });
  }, [ticked, oneRow, me, notify]);

  /** Rollback, the same as QC Batch's (owner 2026-10-19, G5 PlmV2DailyListRollback): one or
   *  several ticked submitted reads — anyone's — are re-opened with ONE required reason (the
   *  form's Rollback endpoint, audited server-side per read); the list reloads. */
  const doRollback = useCallback(async () => {
    if (tickedRows.length === 0) { notify.warning('Select at least one Daily QC to roll back.'); return; }
    const refusal = tickedRows.map(r => dailyListRollbackRefusal(r)).find(Boolean);
    if (refusal) { notify.error(refusal); return; }
    const reason = (await prompt({
      title: 'Rollback',
      message: dailyListRollbackPrompt(tickedRows.map(r => r.dailyQcId ?? '?').join(', ')),
      placeholder: 'Reason',
      required: true,
      confirmLabel: 'Rollback',
    }))?.trim();
    if (!reason) return;
    setRollingBack(true);
    const failed: string[] = [];
    let done = 0;
    try {
      for (const r of tickedRows) {
        try {
          await runLoading(rollbackPlmDailyQc(r.oid, reason), { blocking: true, label: 'Rolling back…' });
          done++;
        } catch (e) {
          failed.push(`${r.dailyQcId ?? '?'}: ${refusalText(e, 'it could not be re-opened.')}`);
        }
      }
    } finally {
      setRollingBack(false);
    }
    if (failed.length > 0) notify.error(failed.join(' | '));
    if (done > 0) notify.success('Rollback completed successfully.');
    setTicked(new Set());
    await load();
  }, [tickedRows, notify, prompt, load]);

  // G5 PlmV2DailyQc_ListView, its eleven columns in its order.
  const columns = useMemo<AppColumn<PlmDailyQcRow>[]>(() => [
    { field: 'dailyQcId', header: 'Daily QC ID', width: 120, text: r => r.dailyQcId ?? '' },
    { field: 'analyzedDate', header: 'Analyzed Date', width: 135, text: r => fmtLocalDateTime(r.analyzedDate ?? r.createdAt) },
    { field: 'analystName', header: 'Analyst', width: 105, text: r => r.analystName ?? '' },
    { field: 'referenceId', header: 'Reference Slide ID', width: 135, text: r => r.referenceId ?? '' },
    { field: 'originalSourceId', header: 'Original Source ID', width: 140, text: r => r.originalSourceId ?? '' },
    { field: 'matrix', header: 'Matrix', width: 90, text: r => r.matrix ?? '' },
    { field: 'source', header: 'Source', width: 140, text: r => r.source ?? '',
      render: r => <span className="plm-dql-clip" title={r.source ?? ''}>{r.source ?? ''}</span> },
    // A read with several devices lists them all: clipped to one short line, the full list on hover.
    { field: 'plmScope', header: 'PLM Scope', width: 150, text: r => r.plmScope ?? '',
      render: r => <span className="plm-dql-clip" title={r.plmScope ?? ''}>{r.plmScope ?? ''}</span> },
    { field: 'scopeAligned', header: 'Scope Aligned', width: 125, align: 'center', text: r => (r.scopeAligned ? '✓' : '—') },
    { field: 'result', header: 'Result', width: 90, type: 'status', text: r => dailyResult(r) },
    { field: 'status', header: 'Status', width: 95, type: 'status', text: r => dailyStatusText(r) },
  ], []);

  // Keyboard (owner 2026-10-19, "shortcut keys to all the PLM forms"): New / Delete / Refresh / Export come
  // with the toolbar (Alt+N / Alt+D / Alt+R / Alt+X); Edit and Rollback are this list's own. Off while a read is open.
  const listKeys: HotkeyDef[] = [
    { combo: 'alt+e', label: 'Edit', group: 'PLM Daily QC', order: 2,
      disabled: !!open || !editGate.enabled || loading || !perm.write, run: doEdit },
    { combo: 'alt+b', label: 'Rollback', group: 'PLM Daily QC', order: 3,
      disabled: !!open || !rollbackGate.enabled || loading || rollingBack || !perm.write, run: () => { void doRollback(); } },
  ];
  useHotkeys(listKeys);

  if (open) {
    return <PlmDailyQcPage oid={open.oid} onBack={() => { setOpen(undefined); void load(); }} />;
  }

  return (
    <div className="plm-root plm-dql-page">
      <div className="dp-breadcrumb">
        <span>Home</span><i className="ti ti-chevron-right" />
        <span>Analysis</span><i className="ti ti-chevron-right" />
        <span className="dp-bc-active">PLM Daily QC</span>
      </div>
      <FormTitle pageId={PAGE_ID} loadMs={loadMs} loading={loading} auditFormName="PLM" />

      <AppToolbar
        onNew={() => { void doNew(); }}
        newDisabled={!!pendingId || loading}
        newDisabledTitle={pendingId ? dailyNewBlockedTip(pendingId) : undefined}
        // G5 order New · Edit · Rollback · Delete: Edit and Rollback ride the `left`
        // slot after New, so each can say why it is off (owner 2026-10-19, G5 parity).
        newFirst
        left={(
          <>
            <button className="ca-btn" onClick={doEdit}
              disabled={!editGate.enabled || loading || !perm.write} title={editGate.enabled ? `${editGate.title} (${formatCombo(effectiveCombo('alt+e'))})` : editGate.title}>
              <span className="tb-i tb-i-blue">✎</span> Edit
            </button>
            <button className="ca-btn plm-tb-keep-icon" onClick={() => { void doRollback(); }}
              disabled={!rollbackGate.enabled || loading || rollingBack || !perm.write}
              title={rollbackGate.enabled ? `${rollbackGate.title} (${formatCombo(effectiveCombo('alt+b'))})` : rollbackGate.title}>
              <i className="ti ti-arrow-back-up" aria-hidden="true" /> Rollback
            </button>
          </>
        )}
        onDelete={() => { void doDelete(); }}
        deleteDisabled={!deleteTarget || loading}
        deleteDisabledTitle="Tick the Daily QC to delete — a pending read that no PLM layer uses"
        perm={perm}
        onRefresh={() => { void load(); }}
        refreshing={loading}
        exportData={{
          filename: 'PLM-Daily-QC',
          headers: columns.map(c => String(c.header)),
          rows: shown.map(r => columns.map(c => c.text?.(r) ?? '')),
        }}
      />

      {/* G5 GateHtml: today's Daily QC of the signed-in analyst — under the toolbar, above the grid (owner 2026-10-17). */}
      {strip && (
        <div className={`plm-dql-strip is-${strip.tone}`} role="status">
          <b>Daily QC</b> · {strip.day} — {strip.text}
        </div>
      )}

      {/* Standard list (QC Batch parity, owner 2026-10-19): the search on its own row below the
          toolbar; the Result chips and the Analyst filter share the filter row in the card. */}
      <div className="list-search-row" data-unsaved-ignore>
        <div className="list-search">
          <i className="ti ti-search" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
          {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
        </div>
      </div>

      <div className="bl-card bl-card-flex plm-dql-card">
        <div className="sa-filter-row">
          <span className="sa-filter-lbl">Result:</span>
          {resultCounts.map(([f, n]) => (
            <button key={f} type="button" className={`sa-filter-btn${result === f ? ' active' : ''}`}
              onClick={() => setResult(f as DailyResultFilter)}>{f} ({n})</button>
          ))}
          <span className="plm-dql-sep" aria-hidden="true" />
          <span className="sa-filter-lbl">Analyst:</span>
          <div className={`plm-dql-analyst${analyst ? ' is-set' : ''}`} data-unsaved-ignore>
            <ComboSelect value={analyst} options={analysts} onChange={setAnalyst} placeholder="All analysts" />
          </div>
        </div>
        <AppDataGrid
          rows={shown}
          columns={columns}
          getRowId={r => r.oid}
          selectedRowId={selected}
          selectable
          selectedIds={ticked}
          onSelectionChange={setTicked}
          onRowClick={r => setSelected(r.oid)}
          onRowDoubleClick={r => setOpen({ oid: r.oid })}
          persistKey="plm-dailyqc-list"
          // Daily QC ID, Analyzed Date, Analyst and Reference Slide ID stay put while the grid scrolls sideways (owner 2026-10-19).
          stickyColumns={4}
          loading={loading}
          defaultPageSize={50}
          emptyText="No records. Press New to start today's Daily QC."
        />
      </div>
      <p className="plm-hint">
        Double-click a read to open it; tick your own pending read and press <b>Edit</b> to continue it, or tick submitted
        reads and press <b>Rollback</b> to re-open them. <b>New</b> starts today's Daily QC: pick a reference slide,
        enter the read, Save, then Submit. The result is shown after Submit.
      </p>
      <PlmDailySlidePickerDialog
        open={picking}
        onClose={() => setPicking(false)}
        onCreated={created => { setPicking(false); setOpen({ oid: created.oid }); }}
      />
    </div>
  );
}
